import { isSameDay, subYears } from "date-fns";
import subDays from "date-fns/subDays";
import PropTypes from "prop-types";
import { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { useIntl } from "react-intl";
import { Dropdown } from "semantic-ui-react";
import { Config } from "../../config/api";
import { date_ranges } from "../campaign/index/models/filter";

const DateRangeControl = ({
  dataTestid,
  filter,
  onChange,
  showReportingPeriod = true,
  beyondCurrentDate = false,
  isClearable,
}) => {
  const intl = useIntl();
  const [reportDateRanges, setReportDateRanges] = useState({
    end_date: filter.end_date ? new Date(filter.end_date) : null,
    start_date: filter.start_date ? new Date(filter.start_date) : null,
  });
  const hasSyncedDate = useRef(false);

  useEffect(() => {
    if (filter.end_date && filter.start_date && !hasSyncedDate.current) {
      hasSyncedDate.current = true;
      setReportDateRanges({
        end_date: new Date(filter.end_date),
        start_date: new Date(filter.start_date),
      });
    }
  }, [filter.end_date, filter.start_date]);

  /**
   * update reporting period
   * @param e
   * @param option
   */
  const daysLookup = {
    "7days": 7,
    "14days": 14,
    "30days": 30,
  };

  const handleReportingPeriod = (_e, option) => {
    const daysToSubtract = daysLookup[option.value] || 1;

    setReportDateRanges({
      end_date: new Date(),
      start_date: subDays(new Date(), daysToSubtract),
    });

    filter.setReportingPeriod(option.value);
  };

  /**
   * handler calendar on change statement
   */
  const onChangeDateRanges = (dates) => {
    const [start, end] = dates;
    // check if date range is cleared
    if (start === null && end === null) {
      filter.resetStartEndDate();
      filter.setReportingPeriod("7days");
      // if date range is same as default, no need to update
      const isSameStartDate = isSameDay(new Date(filter.start_date), reportDateRanges.start_date);
      const isSameEndDate = isSameDay(new Date(filter.end_date), reportDateRanges.end_date);
      if (isSameStartDate && isSameEndDate) return;

      setReportDateRanges({
        end_date: new Date(filter.end_date),
        start_date: new Date(filter.start_date),
      });
      onChange(filter.toJson());
      return;
    }
    setReportDateRanges({
      end_date: end,
      start_date: start,
    });
  };

  /**
   * handler calendar on close
   */
  const onCloseDateRanges = () => {
    const filterDate = { ...reportDateRanges };
    if (reportDateRanges.end_date == null) {
      setReportDateRanges({
        ...reportDateRanges,
        end_date: reportDateRanges.start_date,
      });
      filterDate.end_date = reportDateRanges.start_date;
    }

    filter.setStartDate(filterDate.start_date);
    filter.setEndDate(filterDate.end_date);
    onChange(filter.toJson());
  };

  const CustomInput = forwardRef((props, ref) => {
    return (
      <input
        {...props}
        data-testid={dataTestid ?? "date_range_filter"}
        ref={ref}
        type="text"
      />
    );
  });

  return (
    <DatePicker
      customInput={<CustomInput />}
      dateFormat={Config.dateTimeFormat}
      endDate={reportDateRanges.end_date}
      maxDate={beyondCurrentDate ? undefined : new Date()}
      minDate={subYears(new Date(), 1)}
      onCalendarClose={onCloseDateRanges}
      onChange={onChangeDateRanges}
      selected={reportDateRanges.start_date}
      selectsRange
      startDate={reportDateRanges.start_date}
      isClearable={isClearable}
    >
      {showReportingPeriod && (
        <div>
          <Dropdown
            name="reporting_period"
            onChange={handleReportingPeriod}
            options={date_ranges(intl)}
            selection
            style={{ display: "flex", margin: "5px" }}
            value={filter.reporting_period}
          />
        </div>
      )}
    </DatePicker>
  );
};

DateRangeControl.propTypes = {
  dataTestid: PropTypes.string,
  filter: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  showReportingPeriod: PropTypes.bool,
  beyondCurrentDate: PropTypes.bool,
  isClearable: PropTypes.bool,
};

export default DateRangeControl;
